import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DEAL_STAGES, Modal, inputCls, F, SaveBtn, StatCard, fmtINR, fmtDate } from "./CrmShared";

const blank = { name: "", lead_id: "", value: "", stage: "open", expected_close: "", probability: 50, assigned_to: "", note: "" };

function DealModal({ open, onClose, deal, leads, onSaved }) {
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(deal ? { ...blank, ...deal, note: "" } : blank); }, [open, deal]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f, value: parseFloat(f.value) || 0, probability: parseInt(f.probability) || 0, expected_close: f.expected_close || null };
      if (deal?.id) await api.put(`/store-admin/crm/deals/${deal.id}`, body);
      else await api.post("/store-admin/crm/deals", body);
      toast.success(deal ? "Deal updated" : "Deal created");
      onSaved(); onClose();
    } catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };
  return (
    <Modal open={open} onClose={onClose} title={deal ? "Edit Deal" : "New Deal"} testid="deal-modal">
      <form onSubmit={submit} className="space-y-3">
        <F label="Deal name *"><input required data-testid="deal-name" className={inputCls} value={f.name} onChange={set("name")} /></F>
        <F label="Customer (lead)">
          <select data-testid="deal-lead" className={inputCls} value={f.lead_id} onChange={set("lead_id")}>
            <option value="">— None —</option>
            {leads.map((l) => <option key={l.id} value={l.id}>{l.name}{l.company ? ` · ${l.company}` : ""}</option>)}
          </select>
        </F>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Deal value (₹)"><input type="number" min="0" data-testid="deal-value" className={inputCls} value={f.value} onChange={set("value")} /></F>
          <F label="Stage">
            <select data-testid="deal-stage" className={inputCls} value={f.stage} onChange={set("stage")}>
              {DEAL_STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </F>
          <F label="Expected closing"><input type="date" data-testid="deal-close" className={inputCls} value={f.expected_close} onChange={set("expected_close")} /></F>
          <F label="Probability %"><input type="number" min="0" max="100" data-testid="deal-prob" className={inputCls} value={f.probability} onChange={set("probability")} /></F>
        </div>
        <F label="Assigned user"><input className={inputCls} value={f.assigned_to} onChange={set("assigned_to")} /></F>
        {!deal && <F label="Note"><textarea rows={2} className={`${inputCls} h-auto py-2`} value={f.note} onChange={set("note")} /></F>}
        <SaveBtn saving={saving} label={deal ? "Update Deal" : "Create Deal"} testid="deal-save" />
      </form>
    </Modal>
  );
}

export default function CrmDeals({ refreshKey }) {
  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [edit, setEdit] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/store-admin/crm/deals").then((r) => setDeals(r.data.deals)).catch((e) => toast.error(errMsg(e)));
    api.get("/store-admin/crm/leads", { params: { limit: 500 } }).then((r) => setLeads(r.data.leads)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const del = async (d) => {
    if (!window.confirm(`Delete deal "${d.name}"?`)) return;
    try { await api.delete(`/store-admin/crm/deals/${d.id}`); toast.success("Deal deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (d, stage) => {
    try { await api.put(`/store-admin/crm/deals/${d.id}`, { ...d, stage }); toast.success(`Deal → ${stage}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const sum = (pred) => deals.filter(pred).reduce((a, d) => a + (d.value || 0), 0);

  return (
    <div className="space-y-4" data-testid="crm-deals">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total Pipeline" value={fmtINR(sum((d) => !["won", "lost"].includes(d.stage)))} testid="deal-pipeline" />
        <StatCard label="Won Value" value={fmtINR(sum((d) => d.stage === "won"))} tone="text-emerald-600" testid="deal-won" />
        <StatCard label="Lost Value" value={fmtINR(sum((d) => d.stage === "lost"))} tone="text-red-500" testid="deal-lost" />
        <StatCard label="Pending Value" value={fmtINR(sum((d) => !["won", "lost"].includes(d.stage)))} testid="deal-pending" />
      </div>
      <div className="flex justify-end">
        <button onClick={() => { setEdit(null); setModalOpen(true); }} data-testid="deals-add"
          className="h-10 px-4 rounded-xl bg-foreground text-background text-xs font-extrabold flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> New Deal</button>
      </div>
      {deals.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="deals-empty">No deals yet. Create one from a lead.</div> : (
        <div className="space-y-2">
          {deals.map((d) => (
            <div key={d.id} className="rounded-2xl border border-border bg-background p-4 flex flex-wrap items-center gap-3" data-testid={`deal-row-${d.id}`}>
              <div className="flex-1 min-w-[160px]">
                <p className="font-bold text-sm truncate">{d.name}</p>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {[d.lead_name, d.expected_close ? `closes ${fmtDate(d.expected_close)}` : "", `${d.probability}%`].filter(Boolean).join(" · ")}
                </p>
              </div>
              <span className="text-sm font-extrabold">{fmtINR(d.value)}</span>
              {d.assigned_to && <span className="text-[11px] font-bold text-muted-foreground">@{d.assigned_to}</span>}
              <select value={d.stage} onChange={(e) => move(d, e.target.value)} data-testid={`deal-stage-sel-${d.id}`}
                className="h-8 rounded-lg border border-input bg-background px-2 text-xs font-bold capitalize">
                {DEAL_STAGES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <button onClick={() => { setEdit(d); setModalOpen(true); }} data-testid={`deal-edit-${d.id}`}
                className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => del(d)} data-testid={`deal-del-${d.id}`}
                className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      <DealModal open={modalOpen} onClose={() => setModalOpen(false)} deal={edit} leads={leads} onSaved={load} />
    </div>
  );
}
